/**
 * Animation Controller
 * Handles all GSAP animations for state transitions
 */

import gsap from 'gsap';
import { ANIMATION, STATES } from './constants.js';

export default class AnimationController {
  constructor(renderer, data) {
    this.renderer = renderer;
    this.data = data;
    this.timeline = null;
  }

  /**
   * Animate state transition
   * @param {Object} newState - New state object
   * @param {Object} oldState - Previous state object
   * @param {String} direction - 'forward' or 'backward'
   */
  animateTransition(newState, oldState, direction) {
    // Kill existing timeline
    if (this.timeline) {
      this.timeline.kill();
    }

    this.timeline = gsap.timeline();

    // Determine animation based on state transition
    if (oldState.type === STATES.START && newState.type === STATES.DIMENSION) {
      this.animateStartToDimension(newState.dimension, direction);
    } else if (oldState.type === STATES.DIMENSION && newState.type === STATES.START) {
      this.animateDimensionToStart(direction);
    } else if (oldState.type === STATES.DIMENSION && newState.type === STATES.AREA) {
      this.animateDimensionToArea(oldState, newState, direction);
    } else if (oldState.type === STATES.AREA && newState.type === STATES.DIMENSION) {
      this.animateAreaToDimension(oldState, newState, direction);
    } else if (oldState.type === STATES.AREA && newState.type === STATES.AREA) {
      // Switching between areas (same or different dimension)
      this.animateAreaSwitch(oldState, newState);
    }

    return this.timeline;
  }

  /**
   * Start -> Dimension
   * - Move center circle to dimension position
   * - Fade out start state circles
   * - Stagger in dimension area circles
   */
  animateStartToDimension(dimensionHandle, _direction) {
    const centerPosition = this.renderer.getCenterPosition(dimensionHandle);
    const centerCircles = this.renderer.getCenterCircles();
    const peripheralCircles = this.renderer.getPeripheralCircles();

    // Fade out old circles first
    this.timeline.to('.peripheral-circles circle', {
      scale: 0,
      opacity: 0,
      duration: 0.2,
      ease: 'power2.in',
    });

    // Move center circle
    this.timeline.to(
      centerCircles,
      {
        attr: { cx: centerPosition.cx, cy: centerPosition.cy },
        duration: ANIMATION.centerMoveDuration,
        ease: 'power2.inOut',
      },
      '-=0.1'
    );

    // Stagger in new circles
    this.timeline.fromTo(
      peripheralCircles,
      {
        scale: 0,
        opacity: 0,
        transformOrigin: 'center',
      },
      {
        scale: 1,
        opacity: 1,
        stagger: ANIMATION.circleStaggerDelay,
        duration: ANIMATION.circleAppearDuration,
        ease: 'back.out(1.7)',
      },
      `-=${ANIMATION.centerMoveDuration * 0.5}`
    );
  }

  /**
   * Dimension -> Start
   * - Move center circle back to center
   * - Fade out dimension circles
   * - Fade in start state circles
   */
  animateDimensionToStart(_direction) {
    const centerPosition = this.renderer.getCenterPosition('start');
    const centerCircles = this.renderer.getCenterCircles();
    const peripheralCircles = this.renderer.getPeripheralCircles();

    // Fade out old circles
    this.timeline.to('.peripheral-circles circle', {
      scale: 0,
      opacity: 0,
      stagger: ANIMATION.circleStaggerDelay * 0.5,
      duration: 0.3,
      ease: 'power2.in',
    });

    // Move center circle back
    this.timeline.to(
      centerCircles,
      {
        attr: { cx: centerPosition.cx, cy: centerPosition.cy },
        duration: ANIMATION.centerMoveDuration,
        ease: 'power2.inOut',
      },
      '-=0.2'
    );

    // Fade in new circles
    this.timeline.fromTo(
      peripheralCircles,
      {
        scale: 0,
        opacity: 0,
        transformOrigin: 'center',
      },
      {
        scale: 1,
        opacity: 1,
        stagger: ANIMATION.circleStaggerDelay,
        duration: ANIMATION.circleAppearDuration,
        ease: 'back.out(1.7)',
      },
      `-=${ANIMATION.centerMoveDuration * 0.5}`
    );
  }

  /**
   * Dimension -> Area
   * - Fill the clicked circle and grow it to r=25
   * - Move center circle to area-specific position on triangle edge
   * Note: The circle size is changed in the renderer (r=7 -> r=25)
   */
  animateDimensionToArea(oldState, newState, _direction) {
    const activeCircle = this.renderer.svg.querySelector(
      `circle[data-handle="${newState.area}"]`
    );

    // Animate center circle to area-specific position
    // Get both old (dimension base) and new (area-specific) positions
    const oldCenterPosition = this.renderer.getCenterPosition(
      oldState.dimension,
      oldState.area, // null when coming from dimension state
      this.data
    );
    const newCenterPosition = this.renderer.getCenterPosition(
      newState.dimension,
      newState.area,
      this.data
    );
    const centerCircles = this.renderer.getCenterCircles();

    // Immediately set to old position (before timeline starts) to prevent flash
    // renderState already moved them to new position, so we need to snap back
    gsap.set(centerCircles, {
      attr: { cx: oldCenterPosition.cx, cy: oldCenterPosition.cy },
    });

    if (activeCircle) {
      // Animate the fill color (size is already changed by renderer)
      this.timeline.to(activeCircle, {
        attr: { fill: '#350F4B' },
        duration: 0.3,
        ease: 'back.out(1.7)',
      });
    }

    // Animate to new position
    this.timeline.to(
      centerCircles,
      {
        attr: { cx: newCenterPosition.cx, cy: newCenterPosition.cy },
        duration: ANIMATION.centerMoveDuration,
        ease: 'power2.inOut',
      },
      activeCircle ? '-=0.2' : 0
    );
  }

  /**
   * Area -> Dimension
   * - Unfill the circle and shrink it back to r=7
   * - Move center circle back to dimension base position
   * Note: The circle size is changed in the renderer (r=25 -> r=7)
   */
  animateAreaToDimension(oldState, newState, _direction) {
    const activeCircle = this.renderer.svg.querySelector(
      `circle[data-handle="${oldState.area}"]`
    );

    // Animate center circle back to dimension base position
    // Get both old (area-specific) and new (dimension base) positions
    const oldCenterPosition = this.renderer.getCenterPosition(
      oldState.dimension,
      oldState.area, // area-specific position
      this.data
    );
    const newCenterPosition = this.renderer.getCenterPosition(
      newState.dimension,
      null, // null = dimension base position
      this.data
    );
    const centerCircles = this.renderer.getCenterCircles();

    // Immediately set to old position (before timeline starts) to prevent flash
    // renderState already moved them to new position, so we need to snap back
    gsap.set(centerCircles, {
      attr: { cx: oldCenterPosition.cx, cy: oldCenterPosition.cy },
    });

    if (activeCircle) {
      // Animate the fill color (size is already changed by renderer)
      this.timeline.to(activeCircle, {
        attr: { fill: 'transparent' },
        duration: 0.3,
        ease: 'power2.out',
      });
    }

    // Animate to new position
    this.timeline.to(
      centerCircles,
      {
        attr: { cx: newCenterPosition.cx, cy: newCenterPosition.cy },
        duration: ANIMATION.centerMoveDuration,
        ease: 'power2.inOut',
      },
      activeCircle ? '-=0.2' : 0
    );
  }

  /**
   * Area -> Different Area
   * - Unfill old circle (r=25 -> r=7 in renderer)
   * - Fill new circle (r=7 -> r=25 in renderer)
   * - Move center circle if switching to different dimension
   */
  animateAreaSwitch(oldState, newState) {
    console.log('[AnimationController] Area switch:', {
      oldDimension: oldState?.dimension,
      newDimension: newState?.dimension,
      oldArea: oldState?.area,
      newArea: newState?.area,
    });

    const oldCircle = this.renderer.svg.querySelector(
      `circle[data-handle="${oldState.area}"]`
    );
    const newCircle = this.renderer.svg.querySelector(
      `circle[data-handle="${newState.area}"]`
    );

    // Check if dimensions are different
    const dimensionChanged = oldState?.dimension && newState?.dimension &&
                            oldState.dimension !== newState.dimension;

    console.log('[AnimationController] Dimension changed?', dimensionChanged);

    // If switching to different dimension, immediately set center circle to old position
    // to prevent flash (renderState already moved them to new position)
    if (dimensionChanged) {
      const oldCenterPosition = this.renderer.getCenterPosition(oldState.dimension, oldState.area, this.data);
      const newCenterPosition = this.renderer.getCenterPosition(newState.dimension, newState.area, this.data);
      const centerCircles = this.renderer.getCenterCircles();

      console.log('[AnimationController] Animating center circle to:', newState.dimension, newState.area);
      console.log('[AnimationController] Old center position:', oldCenterPosition);
      console.log('[AnimationController] New center position (area-specific):', newCenterPosition);

      // Immediately set to old position (before timeline starts) to prevent flash
      gsap.set(centerCircles, {
        attr: { cx: oldCenterPosition.cx, cy: oldCenterPosition.cy },
      });

      if (oldCircle) {
        // Animate the fill color (size is already changed by renderer)
        this.timeline.to(oldCircle, {
          attr: { fill: 'transparent' },
          duration: 0.2,
          ease: 'power2.out',
        });
      }

      // Animate to new position
      this.timeline.to(
        centerCircles,
        {
          attr: { cx: newCenterPosition.cx, cy: newCenterPosition.cy },
          duration: ANIMATION.centerMoveDuration,
          ease: 'power2.inOut',
        },
        oldCircle ? '-=0.1' : 0
      );
    } else {
      // Same dimension, different area - subtle movement along axis
      console.log('[AnimationController] Same dimension, animating to area:', newState.area);
      const newCenterPosition = this.renderer.getCenterPosition(newState.dimension, newState.area, this.data);
      const centerCircles = this.renderer.getCenterCircles();

      console.log('[AnimationController] New center position (area offset):', newCenterPosition);

      if (oldCircle) {
        // Animate the fill color (size is already changed by renderer)
        this.timeline.to(oldCircle, {
          attr: { fill: 'transparent' },
          duration: 0.2,
          ease: 'power2.out',
        });
      }

      this.timeline.to(
        centerCircles,
        {
          attr: { cx: newCenterPosition.cx, cy: newCenterPosition.cy },
          duration: ANIMATION.centerMoveDuration * 0.6, // Faster for subtle movement
          ease: 'power2.inOut',
        },
        '-=0.15'
      );
    }

    if (newCircle) {
      // Animate the fill color (size is already changed by renderer)
      this.timeline.to(
        newCircle,
        {
          attr: { fill: '#350F4B' },
          duration: 0.3,
          ease: 'back.out(1.7)',
        },
        dimensionChanged ? `-=${ANIMATION.centerMoveDuration * 0.5}` : '-=0.1'
      );
    }
  }

  /**
   * Animate instant state change (no transition)
   * Used when navigating via browser history or direct page load
   */
  animateInstant(state) {
    // Just update visual state without animation
    gsap.set('.peripheral-circles circle', {
      scale: 1,
      opacity: 1,
    });

    // Set center circle position (with area-specific offset if in AREA state)
    const centerPosition = this.renderer.getCenterPosition(
      state.dimension || 'start',
      state.area,
      this.data
    );
    const centerCircles = this.renderer.getCenterCircles();

    gsap.set(centerCircles, {
      attr: { cx: centerPosition.cx, cy: centerPosition.cy },
    });

    // Set active circle fill if in area state (size already set by renderer)
    if (state.type === STATES.AREA && state.area) {
      const activeCircle = this.renderer.svg.querySelector(
        `circle[data-handle="${state.area}"]`
      );
      if (activeCircle) {
        gsap.set(activeCircle, {
          attr: { fill: '#350F4B' },
        });
      }
    }
  }

  /**
   * Animate entry sequence (first page load)
   * - Triangle spins in with rotation and scale
   * - Center circles pop in
   * - Peripheral circles stagger in
   */
  animateEntrySequence(state) {
    // Kill existing timeline
    if (this.timeline) {
      this.timeline.kill();
    }

    this.timeline = gsap.timeline();

    // Get elements
    const triangle = this.renderer.elements.triangle;
    const centerCircles = this.renderer.getCenterCircles();
    const peripheralCircles = this.renderer.getPeripheralCircles();

    // Get correct center position (with area-specific offset if in AREA state)
    const centerPosition = this.renderer.getCenterPosition(
      state.dimension || 'start',
      state.area,
      this.data
    );

    // Set initial states
    gsap.set(triangle, {
      opacity: 0,
      scale: 0,
      rotation: 360,
      transformOrigin: 'center',
    });

    gsap.set(centerCircles, {
      attr: { cx: centerPosition.cx, cy: centerPosition.cy },
      scale: 0,
      opacity: 0,
      transformOrigin: 'center',
    });

    gsap.set('.peripheral-circles circle', {
      scale: 0,
      opacity: 0,
      transformOrigin: 'center',
    });

    // Animate triangle entrance (spin + scale)
    this.timeline.to(triangle, {
      opacity: 1,
      scale: 1,
      rotation: 0,
      duration: ANIMATION.entrySpinDuration,
      ease: 'back.out(1.7)',
    });

    // Animate center circles pop in (overlapping with triangle)
    this.timeline.to(
      centerCircles,
      {
        scale: 1,
        opacity: 1,
        duration: 0.3,
        ease: 'back.out(1.7)',
      },
      `-=${ANIMATION.entrySpinDuration - ANIMATION.entryCenterDelay}`
    );

    // Animate peripheral circles stagger in
    this.timeline.to(
      peripheralCircles,
      {
        scale: 1,
        opacity: 1,
        stagger: ANIMATION.circleStaggerDelay,
        duration: ANIMATION.circleAppearDuration,
        ease: 'back.out(1.7)',
      },
      `-=${ANIMATION.entrySpinDuration - ANIMATION.entryCircleDelay}`
    );

    // Set active circle fill if in area state (after animation completes)
    if (state.type === STATES.AREA && state.area) {
      this.timeline.set(
        `circle[data-handle="${state.area}"]`,
        {
          attr: { fill: '#350F4B' },
        },
        '>'
      );
    }

    return this.timeline;
  }

  /**
   * Setup hover animations for circles
   * Note: Hover effects are now handled by CSS for better performance
   */
  setupHoverAnimations() {
    // Hover effects now handled by CSS
    // This method kept for potential future enhancements
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.timeline) {
      this.timeline.kill();
    }
  }
}
